import { useEffect, useRef } from 'react';
import { useBrowserStore } from '@/stores/browserStore';
import { useSetupStore } from '@/stores/setupStore';
import { useChatStore } from '@/stores/chatStore';
import { API_BASE } from '@/lib/config';
import { getToken } from '@/stores/authStore';
import { ZOOM_S2S_CREATE_URL, ZOOM_SIGNIN_URL, ZOOM_TAKEOVER_MESSAGE } from '@/lib/zoomSetup';

/** Is this the Zoom sign-in page (where the user must type credentials)? */
export function isZoomSigninUrl(url: string): boolean {
  try {
    const u = new URL(url);
    const host = u.hostname.toLowerCase();
    const path = u.pathname.toLowerCase();
    const isZoomHost = host === 'zoom.us' || host.endsWith('.zoom.us');
    return isZoomHost && (path.startsWith('/signin') || path.startsWith('/login'));
  } catch {
    return false;
  }
}

/** Is this a page outside zoom.us entirely (e.g. accounts.google.com during
 *  Google SSO)? ZAC can never drive there, and the auth-state probe reports
 *  signinPresent=false for them, so no other path releases agent control. */
export function isExternalSsoUrl(url: string): boolean {
  try {
    const u = new URL(url);
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return false;
    const host = u.hostname.toLowerCase();
    return !(host === 'zoom.us' || host.endsWith('.zoom.us'));
  } catch {
    return false;
  }
}

interface ZoomAuthState {
  enabled?: boolean;
  signedIn?: boolean;
  signinPresent?: boolean;
  /** False while the marketplace chrome is still hydrating — the verdict is
   *  "unknown" (both signedIn and signinPresent false); keep polling. */
  pageReady?: boolean;
  url?: string;
  onCreatePage?: boolean;
  s2sFound?: boolean;
  s2sBlocked?: boolean;
}

/** Posted when the account can't create a Server-to-Server app (S2S radio
 *  disabled = the Zoom role lacks the S2S privilege). */
const ZOOM_S2S_BLOCKED_MESSAGE =
  "Heads up — this Zoom account can't create a Server-to-Server OAuth app: " +
  'that option is greyed out, which means your Zoom role is missing the ' +
  '"Server-to-Server OAuth" privilege. Ask your account Owner/Admin to enable ' +
  'it (Zoom Admin → Role Management → Advanced features), then reopen setup and ' +
  "I'll continue from here.";

/**
 * Drives the guided-setup browser based on the user's REAL Zoom auth state.
 *
 * We previously inferred "signed in" from the URL (reaching marketplace
 * /develop or /apps). That was wrong — Zoom serves those pages to logged-out
 * visitors too, so it false-fired the takeover and looped. The authoritative
 * signal is the page itself: the backend `/browser/zoom-auth-state` endpoint
 * reads the live harness DOM for the "Sign in"/"Sign up" affordance (present =
 * logged out) vs its absence (signed in). This hook polls it while the setup
 * step is active and:
 *   - signed in        -> take over: drive to the S2S builder.
 *   - signed out       -> hands off + drive to the sign-in page (once) and ask
 *                         the user to sign in.
 * Stands down while the user has manually taken over (userPaused).
 */
export function useZoomLoginDetect() {
  const panelOpen = useBrowserStore((s) => s.panelOpen);
  const userPaused = useBrowserStore((s) => s.userPaused);
  const zoomSignedIn = useBrowserStore((s) => s.zoomSignedIn);
  const setZoomSignedIn = useBrowserStore((s) => s.setZoomSignedIn);
  const setAgentControl = useBrowserStore((s) => s.setAgentControl);
  const setPendingNavigateUrl = useBrowserStore((s) => s.setPendingNavigateUrl);
  const accessLevel = useSetupStore((s) => s.accessLevel);
  const connected = useSetupStore((s) => s.connected);
  const setAwaitingAppName = useSetupStore((s) => s.setAwaitingAppName);
  const addAssistantMessage = useChatStore((s) => s.addAssistantMessage);
  const firedRef = useRef(false); // takeover fired (once signed in)
  const signedInStreakRef = useRef(0); // consecutive signedIn polls (debounce)
  const signinNavRef = useRef(false); // sign-in prompt+navigate fired (once)
  const warnedRef = useRef(false); // S2S-blocked warning posted (once)
  const namePromptedRef = useRef(false); // app-name chat prompt posted (once)

  useEffect(() => {
    const stepActive = !!accessLevel && !connected;
    const armed = panelOpen && stepActive && !userPaused && !zoomSignedIn;
    if (!armed) {
      // Only fully re-arm (allow a fresh takeover/prompt) once the setup step
      // is no longer active — NOT merely because we took over or the user
      // paused. This prevents re-firing the takeover/prompt on every toggle.
      if (!stepActive) {
        firedRef.current = false;
        signinNavRef.current = false;
        signedInStreakRef.current = 0;
      }
      return;
    }

    let cancelled = false;
    const token = getToken();

    const takeOver = () => {
      if (firedRef.current) return;
      firedRef.current = true;
      setZoomSignedIn(true);
      setAgentControl(true);
      useBrowserStore.getState().setDrivingStatus('Opening the app builder…');
      setPendingNavigateUrl(ZOOM_S2S_CREATE_URL);
      void addAssistantMessage(ZOOM_TAKEOVER_MESSAGE, undefined, undefined, { once: true });
    };

    const promptSignIn = (onSigninPage: boolean) => {
      // Hand control to the user so they can type their credentials.
      setAgentControl(false);
      if (onSigninPage || signinNavRef.current) return;
      signinNavRef.current = true;
      setPendingNavigateUrl(ZOOM_SIGNIN_URL);
      void addAssistantMessage(
        "You're not signed in to Zoom yet — opening the sign-in page. Sign in and I'll take it from there.",
        undefined,
        undefined,
        { once: true },
      );
    };

    // In-flight guard (defect #6): each poll triggers a backend CDP
    // connectOverCDP + page.evaluate (30ms–7.7s observed). Without this, slow
    // polls stack on a fixed interval and hammer the harness Chromium, causing
    // "Execution context was destroyed" races against the takeover navigation.
    // One poll at a time; the interval only fires a new one if the prior settled.
    let polling = false;
    const poll = async () => {
      if (polling) return;
      polling = true;
      try {
        const res = await fetch(`${API_BASE}/browser/zoom-auth-state`, {
          headers: { Authorization: token ? `Bearer ${token}` : '' },
        });
        if (!res.ok) return;
        const data = (await res.json()) as ZoomAuthState;
        if (cancelled || firedRef.current) return;
        // Keep the URL bar honest (the streamed status update can be missed).
        if (data.url && data.url !== useBrowserStore.getState().currentUrl) {
          useBrowserStore.getState().setCurrentUrl(data.url);
        }
        // External SSO (accounts.google.com etc.): ZAC can never act there,
        // and the auth-state probe reports signinPresent=false on non-Zoom
        // pages — so a stuck agentControl overlay would trap the user with
        // no release path (observed live 2026-08-19: unclickable Google
        // sign-in). The user is always the driver on external SSO pages.
        if (isExternalSsoUrl(data.url ?? '')) {
          setAgentControl(false);
        }
        if (!data.enabled) return;
        // Debounce: require TWO consecutive signedIn polls (~2.5s apart) before
        // taking over. The backend already gates signedIn on pageReady, so this
        // is belt-and-suspenders against any single transient frame (e.g. a
        // staged header render) ever triggering a false takeover.
        if (data.signedIn) {
          signedInStreakRef.current += 1;
          if (signedInStreakRef.current >= 2) takeOver();
          return;
        }
        // Not signed in (logged out, OR "unknown" while the page hydrates:
        // signedIn=false AND signinPresent=false). Reset the streak; only act
        // on a definitive signed-out signal, never on the unknown state.
        signedInStreakRef.current = 0;
        if (data.signinPresent) {
          promptSignIn(isZoomSigninUrl(data.url ?? ''));
        }
      } catch {
        /* ignore — try again next tick */
      } finally {
        polling = false;
      }
    };

    void poll();
    const interval = setInterval(poll, 3000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [
    panelOpen,
    accessLevel,
    connected,
    userPaused,
    zoomSignedIn,
    setZoomSignedIn,
    setAgentControl,
    setPendingNavigateUrl,
    addAssistantMessage,
  ]);

  // After takeover (signed in, on the create page) ZAC either:
  //  - warns if the account can't create an S2S app (radio disabled = the Zoom
  //    role lacks the privilege; can't click a disabled control), or
  //  - asks IN CHAT for the app name (interactive input), then the submit
  //    handler creates the app. We prompt for the name rather than guessing it.
  useEffect(() => {
    const stepActive = !!accessLevel && !connected;
    const active = panelOpen && stepActive && zoomSignedIn && !userPaused;
    if (!active) {
      if (!stepActive) {
        warnedRef.current = false;
        namePromptedRef.current = false;
      }
      return;
    }
    if (warnedRef.current || namePromptedRef.current) return;

    let cancelled = false;
    const token = getToken();
    // In-flight guard (defect #6) — see the login-detect poll above.
    let polling = false;
    const poll = async () => {
      if (polling) return;
      polling = true;
      try {
        const res = await fetch(`${API_BASE}/browser/zoom-auth-state`, {
          headers: { Authorization: token ? `Bearer ${token}` : '' },
        });
        if (!res.ok) return;
        const data = (await res.json()) as ZoomAuthState;
        if (cancelled || warnedRef.current || namePromptedRef.current) return;
        if (!data.onCreatePage) return;
        if (data.s2sBlocked) {
          warnedRef.current = true;
          setAgentControl(false); // hands off — nothing to click, let the user read
          useBrowserStore.getState().setDrivingStatus(null);
          void addAssistantMessage(ZOOM_S2S_BLOCKED_MESSAGE, undefined, undefined, { once: true });
          return;
        }
        if (data.s2sFound) {
          // S2S is available — ask the user what to name the app, in chat.
          // ZAC pauses for input, so the driving status clears here.
          namePromptedRef.current = true;
          useBrowserStore.getState().setDrivingStatus(null);
          setAwaitingAppName(true);
          void addAssistantMessage(
            "The builder's ready — what should I name your connection app? " +
              "Use my suggestion or type your own, and I'll create it and handle the next steps.",
            undefined,
            { type: 'input', prompt: 'Name your connection app', suggestion: 'ZAC Connection App' },
            { once: true },
          );
        }
      } catch {
        /* ignore — try again next tick */
      } finally {
        polling = false;
      }
    };
    void poll();
    const interval = setInterval(poll, 4000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [panelOpen, accessLevel, connected, zoomSignedIn, userPaused, setAgentControl, addAssistantMessage, setAwaitingAppName]);
}
